import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { getProductConfig } from '@app/app-updates-product-app';
import type { UiProductType, ProductKey } from '@app/app-updates-product-app';
import SentryLogger from '@app/sentry';

const BUNDLE_DATES_ENDPOINT = '/gateway/api/public/app-updates/v1/bundle-dates';

export const toProductKeysParam = (
  selectedProducts: { id: string }[] | null,
): string | undefined => {
  if (!selectedProducts || selectedProducts.length === 0) {
    return undefined;
  }

  const keys = selectedProducts
    .map(({ id }) => getProductConfig(id as UiProductType)?.productKey)
    .filter((key): key is ProductKey => key !== undefined);

  return keys.join(',') || undefined;
};

export const useBundleDates = (
  selectedProducts: { id: string }[] | null,
): UseQueryResult<string[]> => {
  const { api } = useAppUpdatesContext();
  const productsParam = toProductKeysParam(selectedProducts);

  return useQuery({
    queryKey: [BUNDLE_DATES_ENDPOINT, productsParam ?? ''],
    queryFn: async () => {
      try {
        const response = await api.get<string[]>(BUNDLE_DATES_ENDPOINT, {
          params: productsParam ? { products: productsParam } : {},
        });

        return response.data ?? [];
      } catch (error) {
        SentryLogger.error(error, { extra: { action: 'fetch-bundle-dates' } });

        return [];
      }
    },
  });
};
